import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCorporateIdCard, getCorporateIdCardByEmployeeId } from '../../../../api/endpoints/corporateEmployee';
import type { CorporateIDCard } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IdCardFormDialog } from './IdCardFormDialog';

/** Kimlik kartı CRUD sub-form for the employee detail page. */
export function IdCardSection({ employeeID }: { employeeID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporateIDCard | null>(null);
  const [deleting, setDeleting] = useState<CorporateIDCard | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['corporateEmployee', 'idCards', employeeID],
    queryFn: () => getCorporateIdCardByEmployeeId(employeeID),
  });
  const rows: CorporateIDCard[] = query.data?.corporateIDCard ?? [];

  const deleteMutation = useMutation({
    mutationFn: (card: CorporateIDCard) => deleteCorporateIdCard({ cardID: card.cardID, employeeID }),
    onSuccess: () => {
      enqueueSnackbar('Kimlik kartı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'idCards', employeeID] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1">Kimlik Kartları</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Kimlik Kartı
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kart Numarası</TableCell>
              <TableCell>Ülke</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
                    Kayıtlı kimlik kartı bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.cardID} hover>
                <TableCell>{row.cardNumber}</TableCell>
                <TableCell>{row.country}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <IdCardFormDialog open={createOpen} employeeID={employeeID} card={null} onClose={() => setCreateOpen(false)} />
      <IdCardFormDialog
        open={!!editing}
        employeeID={employeeID}
        card={editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Kimlik kartını sil"
        description={`"${deleting?.cardNumber ?? ''}" numaralı kimlik kartını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
